import { Box } from '@mui/material';
import { MatchAccessibility, MatchPlayersSettings } from '@okletsplay/core';
import { ModalName } from 'app/enums';
import { UserEntity } from 'app/interfaces/entity';
import { modalsReducers } from 'app/store/modal/slice';
import { sessionSelector } from 'app/store/session/selectors';
import { filter, uniqBy } from 'lodash';
import { PlayerTile } from 'modules/users/components/PlayerTile/PlayerTile';
import { SearchUsersModal } from 'modules/users/components/SearchUsersModal';
import React, { FC, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { EmptyUserCard } from 'shared/components/EmptyUserCard';
import { useDefaultUserMenuActions } from 'shared/hooks';
import { useStyles } from './MatchPlayersSelection.styles';
import { useStyles as useScrollStyles } from 'shared/styles/ScrollBar.styles';
import classNames from 'classnames';

interface ComponentProps {
  existingPlayers?: UserEntity[];
  accessibility?: MatchAccessibility;
  playersSettings: MatchPlayersSettings;
  onChange: (value: string[]) => void;
}

export const MatchPlayersSelection: FC<ComponentProps> = ({
  onChange,
  existingPlayers,
  playersSettings,
  accessibility,
}) => {
  const { classes } = useStyles();
  const { classes: scrollBarClasses } = useScrollStyles();
  const dispatch = useDispatch();
  const activeUser = useSelector(sessionSelector.activeUser);

  const { privateMessageActions } = useDefaultUserMenuActions();
  const author = useSelector(sessionSelector.activeUser) as UserEntity;
  const [invitedUsers, setInvitedUsers] = useState<UserEntity[]>(existingPlayers ?? []);

  const updateInvitedUsers = useCallback(
    (newUsers: UserEntity[]) => {
      setInvitedUsers(newUsers);
      onChange?.(newUsers.map((player) => player.userId));
    },
    [onChange]
  );

  const openInviteModal = useCallback(() => {
    dispatch(modalsReducers.openModal({ name: ModalName.SearchUsers }));
  }, [dispatch]);

  const onSearchUsersCloseHandler = useCallback(
    (users: UserEntity[]) => {
      if (!users.length) return;

      const newUsers = uniqBy([...invitedUsers, ...users], 'userId');
      updateInvitedUsers(newUsers);
    },
    [invitedUsers, updateInvitedUsers]
  );

  const removeUserHandler = useCallback(
    (userId: UserEntity['userId']) => {
      const newUsers = filter(invitedUsers, (user) => user.userId !== userId);
      updateInvitedUsers(newUsers);
    },
    [invitedUsers, updateInvitedUsers]
  );

  const getUserActions = useCallback(
    (user: UserEntity) => [
      {
        label: 'Delete',
        handler: () => removeUserHandler(user.userId),
        isAllowedWithoutAuthorization: true,
      },
      ...privateMessageActions,
    ],
    [removeUserHandler, privateMessageActions]
  );

  const withAuthorUsersCount = invitedUsers.length + 1;
  const leftToInviteUsersCount = playersSettings.maxPlayersCount - withAuthorUsersCount;

  return (
    <Box>
      <Box mb={1}>
        <Box className={classes.player}>
          <PlayerTile user={author} />
        </Box>
      </Box>

      <Box className={classNames(classes.listContainer, scrollBarClasses.vertical)}>
        {invitedUsers.map((user) => (
          <React.Fragment key={user.userId}>
            <Box mb={1} mt={1}>
              <Box className={classes.player}>
                <PlayerTile user={user} actions={getUserActions(user)} />
              </Box>
            </Box>
          </React.Fragment>
        ))}

        {leftToInviteUsersCount > 0 ? (
          <>
            <Box mt={1}>
              <Box className={classes.player}>
                <EmptyUserCard
                  title={`Invite ${leftToInviteUsersCount} ${
                    leftToInviteUsersCount > 1 ? 'players' : 'player'
                  } ${accessibility === MatchAccessibility.Public ? '(optional)' : ''}`}
                  onClick={openInviteModal}
                  p={1.875}
                />
              </Box>
            </Box>
          </>
        ) : null}
      </Box>

      <SearchUsersModal
        onClose={onSearchUsersCloseHandler}
        selectionLength={playersSettings.maxPlayersCount - 1}
        activeUsers={invitedUsers}
        hiddenUsers={[activeUser]}
      />
    </Box>
  );
};
